import { createContext, useCallback, useContext, useState } from 'react';
import { ProductVariantBreakdownTypes, SortOption } from './utils';
import { useProductVariantBreakdown } from './useProductVariantBreakdown';
import { useLocalStorageValue } from '@react-hookz/web';

export const QuoteSheetTableContext = createContext<{
  showHistoricalData: boolean;
  setShowHistoricalData: (showHistoricalData: boolean) => void;
  productVariantBreakdown: ProductVariantBreakdownTypes[];
  implicitRestOfBreakdown: ProductVariantBreakdownTypes[];
  onSelectBreakdown: (breakdown: ProductVariantBreakdownTypes[]) => void;
  expandedAggregates: string[];
  toggleAggregate: (aggregateId: string) => void;
  expandAllAggregates: (allAggregateIds: string[]) => void;
  collapseAllAggregates: () => void;
  sortBy: SortOption;
  setSortBy: (sortBy: SortOption) => void;
}>({
  showHistoricalData: false,
  setShowHistoricalData: () => {},
  productVariantBreakdown: [],
  implicitRestOfBreakdown: [],
  onSelectBreakdown: () => {},
  expandedAggregates: [],
  toggleAggregate: () => {},
  expandAllAggregates: () => {},
  collapseAllAggregates: () => {},
  sortBy: 'alphabetical',
  setSortBy: () => {},
});

export function QuoteSheetTableProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showHistoricalData, setShowHistoricalData] = useState(false);
  const [expandedAggregates, setExpandedAggregates] = useState<string[]>([]);
  const { value: sortBy, set: setSortBy } = useLocalStorageValue<SortOption>(
    'quote-sheet-dashboard-sort-by',
    {
      defaultValue: 'alphabetical',
    },
  );

  const {
    productVariantBreakdown,
    implicitRestOfBreakdown,
    onSelectBreakdown,
  } = useProductVariantBreakdown();

  const toggleAggregate = useCallback((aggregateId: string) => {
    setExpandedAggregates((prev) =>
      prev.includes(aggregateId)
        ? prev.filter((id) => id !== aggregateId)
        : [...prev, aggregateId],
    );
  }, []);

  const expandAllAggregates = useCallback((allAggregateIds: string[]) => {
    setExpandedAggregates(allAggregateIds);
  }, []);

  const collapseAllAggregates = useCallback(() => {
    setExpandedAggregates([]);
  }, []);

  return (
    <QuoteSheetTableContext.Provider
      value={{
        showHistoricalData,
        setShowHistoricalData,
        productVariantBreakdown,
        implicitRestOfBreakdown,
        onSelectBreakdown,
        expandedAggregates,
        toggleAggregate,
        expandAllAggregates,
        collapseAllAggregates,
        sortBy: sortBy ?? 'alphabetical',
        setSortBy,
      }}
    >
      {children}
    </QuoteSheetTableContext.Provider>
  );
}

export function useQuoteSheetTableContext() {
  return useContext(QuoteSheetTableContext);
}
